import type { PerpsMarketV2, PerpsMarketV3 } from '@kwenta/sdk/types'
import { PerpsProvider } from '@kwenta/sdk/types'
import { formatDollars, formatNumber, formatPercent, getDisplayAsset } from '@kwenta/sdk/utils'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import Link from 'next/link'
import { type FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import CurrencyIcon from 'components/Currency/CurrencyIcon'
import { Body } from 'components/Text'
import { FlexDivCol, FlexDivRow } from 'components/layout/flex'
import {
	selectMarketAsset,
	selectMarketInfo,
	selectPerpsProvider,
} from 'state/futures/common/selectors'
import type { MarketsDetailProps } from 'state/futures/common/types'
import { useAppSelector } from 'state/hooks'
import { providerIsCrossMargin } from 'utils/futures'

import { CHART_TOGGLE_HEIGHT } from '../styles'

type Props = {
	title: string
	data: [string, string][]
}

const MarketDetailsSecition: FC<Props> = ({ title, data }) => {
	return (
		<SectionWrapper>
			<SectionTitle>{title}</SectionTitle>
			<FlexDivCol rowGap="6px">
				{data.map(([label, value]) => (
					<FlexDivRow key={label + value}>
						<Body color="secondary">{label}:</Body>
						<Body color="secondary">{value}</Body>
					</FlexDivRow>
				))}
			</FlexDivCol>
		</SectionWrapper>
	)
}

type MarketDetailsPageProps = {
	display?: boolean
} & MarketsDetailProps

const MarketDetailsPage: FC<MarketDetailsPageProps> = ({ display, marketsDetail }) => {
	const { t } = useTranslation()
	const marketAsset = useAppSelector(selectMarketAsset)
	const marketInfo = useAppSelector(selectMarketInfo)
	const perpsProvider = useAppSelector(selectPerpsProvider)
	const asset = getDisplayAsset(marketAsset) ?? 'ETH'

	const marketData = useMemo(() => {
		return get(marketsDetail, asset) ?? marketsDetail.ETH
	}, [marketsDetail, asset])

	return (
		<MarketDetailPageWrapper rowGap="30px" $display={display}>
			<Container rowGap="30px">
				<FlexDivRow columnGap="15px" justifyContent="flex-start">
					<StyledCurrencyIcon currencyKey={marketAsset} width={30} height={30} />
					<FlexDivCol rowGap="2px">
						<MarketTitle color="primary" size="large" weight="bold">
							{asset}
						</MarketTitle>
						<Body color="secondary">{marketData.name}</Body>
					</FlexDivCol>
				</FlexDivRow>
				<DetailsContainer>
					<FlexDivCol rowGap="30px">
						<SectionWrapper>
							<SectionTitle>{t('futures.market.details.about')}</SectionTitle>
							{marketData.description.split('\n').map((desc) => (
								<Body key={desc} color="secondary">
									{desc}
								</Body>
							))}
						</SectionWrapper>
						{!isEmpty(marketData.links) && (
							<SectionWrapper>
								<SectionTitle>{t('futures.market.details.relevant-links')}</SectionTitle>
								<FlexDivRow columnGap="15px" justifyContent="start">
									{map(marketData.links, (link, key) => (
										<Link key={key} href={link} target="_blank" rel="noreferrer">
											<Body key={key} color="preview" as="a" weight="semiBold">
												{key}
											</Body>
										</Link>
									))}
								</FlexDivRow>
							</SectionWrapper>
						)}
						{marketInfo && (
							<MarketDetailsSecition
								title={t('futures.market.details.trade-details')}
								data={[
									[
										t('futures.market.details.maker-taker-fees'),
										`${formatPercent(marketInfo.feeRates.makerFee)} / ${formatPercent(
											marketInfo.feeRates.takerFee
										)}`,
									],
									[
										t('futures.market.details.max-market-value'),
										formatDollars(marketInfo.marketLimitUsd.long, { maxDecimals: 0 }),
									],
									[
										t('futures.market.details.min-position-size'),
										perpsProvider === PerpsProvider.SNX_V2_OP ? '$50 SUSD' : '$100 USDC',
									],
								]}
							/>
						)}
					</FlexDivCol>
					<FlexDivCol rowGap="30px">
						{perpsProvider === PerpsProvider.SNX_V2_OP && marketInfo && (
							<MarketDetailsSecition
								title={t('futures.market.details.contract-configuration')}
								data={[
									[t('futures.market.details.skew-scale'), formatNumber(marketInfo.marketSkew)],
									[
										t('futures.market.details.liquidity-buffer-ratio'),
										`${(marketInfo as PerpsMarketV2<string>).settings.liquidationBufferRatio} bp`,
									],
									[
										t('futures.market.details.liquidity-premium-multiplier'),
										`${(marketInfo as PerpsMarketV2<string>).settings.liquidationPremiumMultiplier}`,
									],
									[
										t('futures.market.details.offchain-delayed-order-min-age'),
										`${(marketInfo as PerpsMarketV2<string>).settings.offchainDelayedOrderMinAge} sec`,
									],
									[
										t('futures.market.details.offchain-delayed-order-max-age'),
										`${(marketInfo as PerpsMarketV2<string>).settings.offchainDelayedOrderMaxAge} sec`,
									],
									[
										t('futures.market.details.max-funding-velocity'),
										`${(marketInfo as PerpsMarketV2<string>).settings.maxFundingVelocity}`,
									],
									[
										t('futures.market.details.max-leverage'),
										`${(marketInfo as PerpsMarketV2<string>).appMaxLeverage}`,
									],
									[
										t('futures.market.details.offchain-price-divergence'),
										`${formatPercent(
											(marketInfo as PerpsMarketV2<string>).settings.offchainPriceDivergence,
											{
												minDecimals: 0,
											}
										)}`,
									],
									[
										t('futures.market.details.max-liquidation-delta'),
										`${(marketInfo as PerpsMarketV2<string>).settings.maxLiquidationDelta}`,
									],
									[
										t('futures.market.details.max-pd'),
										`${(marketInfo as PerpsMarketV2<string>).settings.maxPD}`,
									],
								]}
							/>
						)}
						{perpsProvider === PerpsProvider.SNX_V2_OP && marketInfo && (
							<MarketDetailsSecition
								title={t('futures.market.details.market-parameters')}
								data={[
									[
										t('futures.market.details.delayed-order-confirm-window'),
										`${(marketInfo as PerpsMarketV2<string>).settings.delayedOrderConfirmWindow} sec`,
									],
									[
										t('futures.market.details.min-delay-time-delta'),
										`${(marketInfo as PerpsMarketV2<string>).settings.minDelayTimeDelta} sec`,
									],
									[
										t('futures.market.details.max-delay-time-delta'),
										`${formatNumber(
											(marketInfo as PerpsMarketV2<string>).settings.maxDelayTimeDelta,
											{
												minDecimals: 0,
											}
										)} sec`,
									],
								]}
							/>
						)}
						{providerIsCrossMargin(perpsProvider) && marketInfo && (
							<MarketDetailsSecition
								title={t('futures.market.details.contract-configuration')}
								data={[
									[t('futures.market.details.skew-scale'), formatNumber(marketInfo.marketSkew)],
									[
										t('futures.market.details.minimum-initial-margin-ratio'),
										`${(marketInfo as PerpsMarketV3<string>).settings.minimumInitialMarginRatio}`,
									],
									[
										t('futures.market.details.minimum-position-margin'),
										`${(marketInfo as PerpsMarketV3<string>).settings.minimumPositionMargin}`,
									],
									[
										t('futures.market.details.maintenance-margin-scalar'),
										`${(marketInfo as PerpsMarketV3<string>).settings.maintenanceMarginScalar}`,
									],
									[
										t('futures.market.details.max-liquidation-limit-accumulation-multiplier'),
										`${(marketInfo as PerpsMarketV3<string>).settings.maxLiquidationLimitAccumulationMultiplier}`,
									],
									[
										t('futures.market.details.max-seconds-in-liquidation-window'),
										`${(marketInfo as PerpsMarketV3<string>).settings.maxSecondsInLiquidationWindow} sec`,
									],
									[
										t('futures.market.details.flag-reward-ratio'),
										`${(marketInfo as PerpsMarketV3<string>).settings.flagRewardRatio}`,
									],
									[
										t('futures.market.details.initial-margin-ratio'),
										`${(marketInfo as PerpsMarketV3<string>).settings.initialMarginRatio}`,
									],
									[
										t('futures.market.details.interest-rate'),
										`${(marketInfo as PerpsMarketV3<string>).settings.interestRate}`,
									],
								]}
							/>
						)}
					</FlexDivCol>
				</DetailsContainer>
				<Disclaimer color="secondary">{t('futures.market.details.disclaimer')}</Disclaimer>
			</Container>
		</MarketDetailPageWrapper>
	)
}

const MarketDetailPageWrapper = styled(FlexDivCol)<{ $display?: boolean }>`
	width: 100%;
	height: calc(100% - ${CHART_TOGGLE_HEIGHT}px);
	padding: 15px;
	${(props) =>
		!props.$display &&
		css`
			display: none;
		`}
	color: ${(props) => props.theme.colors.selectedTheme.white};
`

const MarketTitle = styled(Body)`
	font-size: 18px;
	font-weight: 600;
`

const SectionWrapper = styled(FlexDivCol)`
	row-gap: 15px;
`

const SectionTitle = styled(Body)`
	font-size: 15px;
	font-weight: 700;
`

const StyledCurrencyIcon = styled(CurrencyIcon)`
	border-radius: 50%;
	border: 1px solid #ffffff;
	width: 32px;
	height: 32px;
`

const Container = styled(FlexDivCol)`
	overflow-y: scroll;
	height: 100%;
`

const DetailsContainer = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	height: 100%;
	grid-gap: 30px;
	color: ${({ theme }) => theme.colors.white};
	& > div {
		border-right: 1px solid ${(props) => props.theme.colors.selectedTheme.newTheme.border.color};
	}

	& > div:first-child {
		padding-right: 30px;
	}

	& > div:last-child {
		border-right: none;
	}
`

const Disclaimer = styled(Body)`
	text-align: center;
`

export default MarketDetailsPage
